import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Users, Briefcase, FileText, CheckSquare, Clock, CheckCircle, Calendar, FileCheck, History } from "lucide-react";
import axios from "axios";
import { API } from "../config/api";
import { getStageColor } from "../utils/helpers";
import { format } from "date-fns";
import { ro } from "date-fns/locale";

export default function AdminDashboard() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, getAuthHeader());
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse font-body text-muted-foreground">Se încarcă dashboard-ul...</div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Clienți",
      value: stats?.total_clients || 0,
      icon: Briefcase,
      color: "text-info",
      bgColor: "bg-info/10",
      link: "/clients",
    },
    {
      title: "Dosare Active",
      value: stats?.total_cases || 0,
      icon: FileText,
      color: "text-primary",
      bgColor: "bg-primary/10",
      link: "/cases",
    },
    {
      title: "Membri Echipă",
      value: stats?.total_team || 0,
      icon: Users,
      color: "text-success",
      bgColor: "bg-success/10",
      link: "/team",
    },
    {
      title: "Sarcini în Așteptare",
      value: stats?.pending_tasks || 0,
      icon: Clock,
      color: "text-warning",
      bgColor: "bg-warning/10",
      link: "/tasks",
    },
    {
      title: "Cereri Documente",
      value: stats?.pending_doc_requests || 0,
      icon: FileCheck,
      color: "text-accent",
      bgColor: "bg-accent/10",
      link: "/document-requests",
    },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="font-body text-muted-foreground mt-1">
          Bine ai revenit! Iată o prezentare generală a cabinetului de avocatură.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat) => (
          <Card 
            key={stat.title} 
            className={`card-hover ${stat.link ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} 
            data-testid={`stat-${stat.title.toLowerCase().replace(" ", "-")}`}
            onClick={() => stat.link && navigate(stat.link)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <div>
                  <p className="font-body text-sm text-muted-foreground">{stat.title}</p>
                  <p className="font-mono text-2xl font-bold">{stat.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Court Dates - Grouped by date */}
        <Card data-testid="upcoming-dates-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Calendar className="w-5 h-5 text-accent" />
              Termene Viitoare
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/calendar">Vezi toate</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {stats?.upcoming_court_dates?.length === 0 ? (
              <p className="text-center text-muted-foreground py-6 font-body">
                Nu există termene viitoare.
              </p>
            ) : (
              <div className="space-y-4">
                {(() => {
                  // Group by date
                  const grouped = {};
                  (stats?.upcoming_court_dates || []).forEach(term => {
                    const d = term.date;
                    if (!grouped[d]) grouped[d] = [];
                    grouped[d].push(term);
                  });
                  return Object.entries(grouped).map(([date, terms]) => (
                    <div key={date}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <p className="font-mono text-sm font-bold text-foreground">
                          {format(new Date(date), "EEEE, dd/MM/yyyy", { locale: ro })}
                        </p>
                        <Badge variant="secondary" className="text-xs ml-auto">{terms.length}</Badge>
                      </div>
                      <div className="ml-4 space-y-0.5">
                        {terms.map((term, idx) => (
                          <Link
                            key={idx}
                            to={term.case_type === "cases_bc" ? `/dosare-bc?open=${term.case_id}` : `/cases?open=${term.case_id}`}
                            className="flex items-center gap-2 py-1.5 px-2 rounded transition-colors border-l-[3px] border-l-orange-400 bg-orange-50/50 hover:bg-orange-50 group"
                            data-testid={`upcoming-term-${date}-${idx}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
                            <span className="font-mono text-sm font-medium truncate">{term.numar_dosar}</span>
                            {term.case_type === "cases_bc" && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 text-amber-700 border-amber-300 shrink-0">BC</Badge>
                            )}
                            <span className="text-muted-foreground mx-1 shrink-0">|</span>
                            <span className="font-body text-sm text-muted-foreground truncate">{term.clients_display !== "N/A" ? term.clients_display : "-"}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Past Court Dates - Grouped by date, color coded */}
        <Card data-testid="past-dates-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <History className="w-5 h-5 text-muted-foreground" />
              Termene Anterioare
            </CardTitle>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Cu soluție finală</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> Fără soluție finală</span>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.past_court_dates?.length === 0 ? (
              <p className="text-center text-muted-foreground py-6 font-body">
                Nu există termene anterioare în ultima săptămână.
              </p>
            ) : (
              <div className="space-y-4">
                {(() => {
                  const grouped = {};
                  (stats?.past_court_dates || []).forEach(term => {
                    const d = term.date;
                    if (!grouped[d]) grouped[d] = [];
                    grouped[d].push(term);
                  });
                  return Object.entries(grouped).map(([date, terms]) => (
                    <div key={date}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <p className="font-mono text-sm font-bold text-muted-foreground">
                          {format(new Date(date), "EEEE, dd/MM/yyyy", { locale: ro })}
                        </p>
                        <Badge variant="secondary" className="text-xs ml-auto">{terms.length}</Badge>
                      </div>
                      <div className="ml-4 space-y-0.5">
                        {terms.map((term, idx) => {
                          const hasSolution = !!term.last_term_has_solutie;
                          return (
                            <Link
                              key={idx}
                              to={term.case_type === "cases_bc" ? `/dosare-bc?open=${term.case_id}` : `/cases?open=${term.case_id}`}
                              className={`flex items-center gap-2 py-1.5 px-2 rounded transition-colors border-l-[3px] ${
                                hasSolution 
                                  ? "border-l-green-500 bg-green-50/50 hover:bg-green-50" 
                                  : "border-l-amber-400 bg-amber-50/50 hover:bg-amber-50"
                              }`}
                              data-testid={`past-term-${date}-${idx}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${hasSolution ? "bg-green-500" : "bg-amber-400"}`} />
                              <span className="font-mono text-sm font-medium truncate">{term.numar_dosar}</span>
                              {term.case_type === "cases_bc" && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 text-amber-700 border-amber-300 shrink-0">BC</Badge>
                              )}
                              <span className="text-muted-foreground mx-1 shrink-0">|</span>
                              <span className="font-body text-sm text-muted-foreground truncate">{term.clients_display !== "N/A" ? term.clients_display : "-"}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Cases - compact line format */}
        <Card data-testid="recent-cases-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Dosare Recente
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/cases">Vezi toate</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {stats?.recent_cases?.length === 0 ? (
              <p className="text-center text-muted-foreground py-6 font-body">
                Nu există dosare încă.
              </p>
            ) : (
              <div className="space-y-0.5">
                {stats?.recent_cases?.map((caseItem) => (
                  <Link
                    key={caseItem.id}
                    to={`/cases?open=${caseItem.id}`}
                    className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-secondary/50 transition-colors"
                    data-testid={`recent-case-${caseItem.id}`}
                  >
                    <span className="font-mono text-sm font-medium truncate">{caseItem.numar_dosar}</span>
                    <span className="text-muted-foreground mx-1 shrink-0">|</span>
                    <span className="font-body text-sm text-muted-foreground truncate flex-1">{caseItem.clients_display || "-"}</span>
                    <Badge className={`font-body text-[10px] shrink-0 ${getStageColor(caseItem.etapa_procesuala)}`}>
                      {caseItem.etapa_procesuala}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Task Summary & Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card data-testid="task-summary-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-sm text-muted-foreground">Rata de Finalizare Sarcini</p>
                <p className="font-mono text-3xl font-bold mt-1">
                  {stats?.completed_tasks + stats?.pending_tasks > 0
                    ? Math.round(
                        (stats?.completed_tasks / (stats?.completed_tasks + stats?.pending_tasks)) *
                          100
                      )
                    : 0}
                  %
                </p>
              </div>
              <div className="p-4 bg-success/10 rounded-full">
                <CheckSquare className="w-8 h-8 text-success" />
              </div>
            </div>
            <div className="mt-4 h-2 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-success transition-all duration-500"
                style={{
                  width: `${
                    stats?.completed_tasks + stats?.pending_tasks > 0
                      ? (stats?.completed_tasks / (stats?.completed_tasks + stats?.pending_tasks)) *
                        100
                      : 0
                  }%`,
                }}
              />
            </div>
            <div className="flex justify-between mt-2 text-sm">
              <span className="text-muted-foreground">{stats?.pending_tasks || 0} în așteptare</span>
              <span className="text-success">{stats?.completed_tasks || 0} finalizate</span>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="quick-actions-card">
          <CardContent className="p-6">
            <p className="font-heading text-lg font-semibold mb-4">Acțiuni Rapide</p>
            <div className="grid grid-cols-2 gap-3">
              <Link
                to="/clients"
                className="flex items-center gap-2 p-3 rounded-lg border hover:bg-secondary transition-colors font-body text-sm"
              >
                <Briefcase className="w-4 h-4" />
                Adaugă Client
              </Link>
              <Link
                to="/cases"
                className="flex items-center gap-2 p-3 rounded-lg border hover:bg-secondary transition-colors font-body text-sm"
              >
                <FileText className="w-4 h-4" />
                Dosar Nou
              </Link>
              <Link
                to="/tasks"
                className="flex items-center gap-2 p-3 rounded-lg border hover:bg-secondary transition-colors font-body text-sm"
              >
                <CheckSquare className="w-4 h-4" />
                Atribuie Sarcină
              </Link>
              <Link
                to="/calendar"
                className="flex items-center gap-2 p-3 rounded-lg border hover:bg-secondary transition-colors font-body text-sm"
              >
                <Calendar className="w-4 h-4" />
                Calendar
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
